/**
 * Upcycli redesign — persistent filter access.
 *
 * Reveals `<upc-filter-dock>` once the in-flow filter row has scrolled above
 * the viewport, and hides it again when the user scrolls back to it, so there
 * is never a duplicated affordance on screen.
 *
 * Breakpoint handling is deliberately implicit rather than a matchMedia
 * branch: both anchors are always in the DOM, and the one hidden at the
 * current breakpoint reports an all-zero rect (`bottom === 0`), which the
 * strict `< 0` test never satisfies. Resizing therefore needs no extra work.
 *
 * Also publishes `--upc-bottom-stack`, the measured height of any fixed bottom
 * bar (the Braderie offer bar), so the mobile pill stacks above it instead of
 * landing on top of it.
 */

const ANCHOR_SELECTOR = '[data-upc-filter-anchor]';
const BOTTOM_BAR_SELECTOR = '.upc-offer-bar';

class UpcFilterDock extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {ResizeObserver | undefined} */
  #bottomBarObserver;

  /** @type {Element[]} */
  #anchors = [];

  connectedCallback() {
    // Without the drawer there is nothing to open, so stay out of the way.
    if (!document.querySelector('#filters-drawer')) return;

    this.#anchors = Array.from(document.querySelectorAll(ANCHOR_SELECTOR));
    if (!this.#anchors.length) return;

    // Resolve the state synchronously first: the section re-renders on every
    // filter change, and waiting for the observer's first async callback would
    // flash the dock out and back in each time.
    this.#update();

    this.#observer = new IntersectionObserver(() => this.#update(), { threshold: 0 });
    for (const anchor of this.#anchors) this.#observer.observe(anchor);

    this.#measureBottomBar();
    document.addEventListener('cart:update', this.#onCartUpdate);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#bottomBarObserver?.disconnect();
    document.removeEventListener('cart:update', this.#onCartUpdate);
  }

  #update = () => {
    // Strictly above the viewport — a display:none anchor reports 0 and is
    // correctly read as "not scrolled past".
    const scrolledPast = this.#anchors.some((anchor) => anchor.getBoundingClientRect().bottom < 0);
    this.dataset.state = scrolledPast ? 'visible' : 'hidden';
  };

  // The offer bar re-renders on cart changes, so re-measure after the swap.
  #onCartUpdate = () => {
    requestAnimationFrame(() => this.#measureBottomBar());
  };

  #measureBottomBar() {
    this.#bottomBarObserver?.disconnect();

    const bar = document.querySelector(BOTTOM_BAR_SELECTOR);
    const publish = (height) => {
      document.documentElement.style.setProperty('--upc-bottom-stack', `${Math.round(height)}px`);
    };

    if (!bar) {
      publish(0);
      return;
    }

    publish(bar.getBoundingClientRect().height);

    // Border box, not contentRect: the bar's own padding is part of the space
    // the pill has to clear.
    this.#bottomBarObserver = new ResizeObserver(([entry]) => {
      if (entry) publish(entry.target.getBoundingClientRect().height);
    });
    this.#bottomBarObserver.observe(bar);
  }
}

if (!customElements.get('upc-filter-dock')) {
  customElements.define('upc-filter-dock', UpcFilterDock);
}
